import React from 'react';

import dayjs from 'dayjs';

import { Alert, Stack, Typography } from '@mui/material';

import { toPromoBrand } from '~/consts/promoBrand';

import { useListVoucherLogs } from '~/hooks/apis/promo/useListVoucherLogs';
import { useListVoucherUsers } from '~/hooks/apis/promo/useListVoucherUsers';

import { Voucher } from '~/services/PromoService';

import { getVoucherLogWarningsSummary } from '~/utils/voucherLogWarnings';

const WARNING_PAGE_SIZE = 100;

type Props = { voucher: Voucher; brand: string; userId?: string; orderId?: string };

export default function PromoTesterVoucherWarnings({ voucher, brand, userId, orderId }: Props) {
  const users = useListVoucherUsers({
    voucherId: userId ? voucher.voucherId : undefined,
    query: {
      user_id: userId,
      brand: toPromoBrand(brand),
      includeExpired: true,
      includeUsed: true,
      limit: WARNING_PAGE_SIZE,
    },
  });
  const logs = useListVoucherLogs({
    query: { voucherId: voucher.voucherId, brand: toPromoBrand(brand), orderId, limit: WARNING_PAGE_SIZE },
    enabled: Boolean(orderId),
  });
  const assignments = users.data?.result.voucherUsers ?? [];
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">{voucher.voucherName}</Typography>
      {(users.isFetching || logs.isFetching) && <Typography variant="body2">Checking assignments and logs…</Typography>}
      {[users.error, logs.error].filter(Boolean).map((error, index) => (
        <Alert key={index} severity="error">
          Could not check voucher: {error.message}
        </Alert>
      ))}
      {!userId && <Typography variant="body2">Load an order customer to check voucher assignments.</Typography>}
      {users.isSuccess && assignments.length === 0 && (
        <Alert severity="warning">No assignment for this order customer.</Alert>
      )}
      {assignments.map((assignment) => (
        <React.Fragment key={assignment.voucherUserId}>
          {assignment.usedAt && (
            <Alert severity="info">
              Assignment {assignment.voucherUserId} was used at {assignment.usedAt}.
            </Alert>
          )}
          {assignment.expiredAt && dayjs(assignment.expiredAt).isBefore(dayjs()) && (
            <Alert severity="warning">
              Assignment {assignment.voucherUserId} expired at {assignment.expiredAt}.
            </Alert>
          )}
        </React.Fragment>
      ))}
      {getVoucherLogWarningsSummary(logs.data?.result).map(({ voucherLogId, warning }, index) => (
        <Alert
          key={`${voucherLogId}-${index}`}
          severity={warning.level === 'error' ? 'error' : warning.level === 'info' ? 'info' : 'warning'}
        >
          Log {voucherLogId}: {warning.message || warning.code}
        </Alert>
      ))}
      {((users.data?.result.total ?? 0) > WARNING_PAGE_SIZE || (logs.data?.result.total ?? 0) > WARNING_PAGE_SIZE) && (
        <Alert severity="info">
          Only the first {WARNING_PAGE_SIZE} assignments and logs were checked. Open the voucher for the full history.
        </Alert>
      )}
    </Stack>
  );
}
